<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Interessanter Background passend zur Reitsberger-Hof Webseite -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background - natürliche Farben für Bauernhof-Thema -->
      <div class="absolute inset-0 bg-gradient-to-br from-blue-50 via-green-50/30 via-white to-amber-50/40 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"></div>
      
      <!-- Animierte Blobs in natürlichen Farben - passend zum Bauernhof-Thema -->
      <!-- Roter Blob (Brand-Farbe) -->
      <div class="absolute top-0 left-0 w-[500px] h-[500px] bg-[#e5544b]/8 dark:bg-[#e5544b]/5 rounded-[60%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-60 animate-blob"></div>
      
      <!-- Grüner Blob (Felder/Gras) -->
      <div class="absolute top-1/4 right-0 w-[600px] h-[600px] bg-green-400/10 dark:bg-green-500/6 rounded-[50%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>
      
      <!-- Gelber Blob (Getreide) -->
      <div class="absolute bottom-0 left-1/4 w-[550px] h-[550px] bg-amber-300/12 dark:bg-amber-400/6 rounded-[55%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-45 animate-blob animation-delay-4000"></div>
      
      <!-- Zusätzlicher sanfter Blob für Tiefe -->
      <div class="absolute top-1/2 right-1/3 w-[400px] h-[400px] bg-blue-300/8 dark:bg-blue-400/4 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-35 animate-blob-organic animation-delay-1500"></div>
      
      <!-- Grid Pattern Overlay für moderne Struktur -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] opacity-60"></div>
      
      <!-- Subtile Wellen-Effekte für natürlichen Look -->
      <div class="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-green-100/20 via-transparent to-transparent dark:from-green-900/10"></div>
    </div>

    <Navigation />

    <!-- Hero Section - organischerer Stil -->
    <section class="pt-36 pb-16 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col lg:flex-row items-center gap-12 mb-12">
          <!-- Text Content -->
          <div class="flex-1 text-center lg:text-left">
            <div v-if="logoUrl" class="flex justify-center lg:justify-start mb-8 reveal">
              <img 
                :key="`reitsberger-hof-logo-${isDark}-${logoUrl}`"
                :src="logoUrl"
                alt="Reitsberger Hof Logo" 
                class="h-20 md:h-24 object-contain transition-opacity duration-300"
              />
            </div>
            <div class="inline-block mb-6 reveal">
              <div class="px-5 py-2.5 bg-white dark:bg-gray-800/80 backdrop-blur-md rounded-full border-2 border-green-400/30 dark:border-green-500/30 shadow-md">
                <span class="text-sm font-medium text-gray-800 dark:text-white">🌾 Erlebnisbauernhof</span>
              </div>
            </div>
            <h1 class="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-6 reveal delay-100 leading-tight">
              Reitsberger Hof
            </h1>
            <div class="mt-4 h-2 w-32 bg-[#e5544b] mx-auto lg:mx-0 rounded-full reveal delay-200"></div>
            <p class="mt-6 text-xl md:text-2xl font-medium text-gray-700 dark:text-white reveal delay-300">
              Moderne Webpräsenz für einen familiengeführten Betrieb
            </p>
            <div class="mt-8 text-lg text-gray-600 dark:text-gray-400 leading-relaxed reveal delay-400 space-y-4">
              <p>
                Konzeption und Umsetzung einer <span class="font-bold">modernen Webpräsenz</span> für den Reitsberger-Hof, einen familiengeführten landwirtschaftlichen Betrieb in Vaterstetten. Als Teil der Familie habe ich den Webauftritt des Hofes vollständig übernommen und modernisiert.
              </p>
              <p>
                Die Webseite präsentiert den Hof, seine Werte und sein Angebot in einem <span class="font-bold">klar strukturierten, nutzerfreundlichen Design</span>. Ziel war es, Tradition und Regionalität mit einer modernen digitalen Außendarstellung zu verbinden.
              </p>
              <p class="text-base font-semibold">
                Umsetzung mit <span class="text-[#e5544b] dark:text-red-400">WordPress</span>, Fokus auf Performance, Responsive Design und einfache Pflege der Inhalte.
              </p>
            </div>
          </div>

          <!-- Hero Screenshot -->
          <div class="flex-1 w-full max-w-2xl reveal delay-300">
            <div class="relative group">
              <div class="absolute -inset-4 bg-gradient-to-r from-[#e5544b]/20 to-red-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition duration-500"></div>
              <div class="relative bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-2xl border-2 border-gray-200 dark:border-gray-700 cursor-pointer" @click="openLightbox('/projects/reitsberger-hof/hero.webp')">
                <img 
                  src="/projects/reitsberger-hof/hero.webp" 
                  alt="Reitsberger Hof Webseite" 
                  class="w-full h-auto object-cover hover:scale-105 transition duration-700"
                />
                <div class="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div class="bg-white/20 backdrop-blur-md p-3 rounded-full">
                    <MagnifyingGlassPlusIcon class="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
            <!-- CTA Button unter dem Hero Image -->
            <div class="flex justify-center mt-6 reveal delay-500">
              <a 
                href="https://www.reitsberger-hof.de" 
                target="_blank" 
                rel="noopener noreferrer"
                class="inline-flex items-center space-x-3 px-8 py-4 bg-[#e5544b] text-white font-bold rounded-2xl shadow-xl shadow-red-900/20 hover:bg-red-600 dark:hover:bg-red-500 transition-all duration-300 hover:scale-105 group"
              >
                <GlobeAltIcon class="w-6 h-6 group-hover:rotate-12 transition-transform" />
                <span>Zur Webseite</span>
              </a>
            </div>
          </div>
        </div>

        <!-- Tech Stack -->
        <div class="mb-12 reveal delay-500">
          <div class="flex flex-wrap justify-center gap-2 md:gap-3">
            <span
              v-for="tech in displayedTechnologies"
              :key="tech"
              class="px-2 py-1 md:px-4 md:py-2 bg-white dark:bg-gray-800 backdrop-blur-sm rounded-full md:rounded-full text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-md md:shadow-lg border-2 border-blue-200/50 dark:border-gray-600/50 hover:border-green-400 dark:hover:border-green-500 hover:shadow-xl transition-all duration-300"
            >
              {{ tech }}
            </span>
          </div>
          <!-- "Mehr anzeigen" Button nur auf mobil -->
          <button
            v-if="!showAllTechs && project.technologies.length > 8"
            @click="showAllTechs = true"
            class="mt-3 mx-auto block md:hidden text-sm font-semibold text-[#e5544b] dark:text-red-400 hover:underline transition-colors"
          >
            + {{ project.technologies.length - 8 }} weitere anzeigen
          </button>
          <button
            v-else-if="showAllTechs"
            @click="showAllTechs = false"
            class="mt-3 mx-auto block md:hidden text-sm font-semibold text-[#e5544b] dark:text-red-400 hover:underline transition-colors"
          >
            Weniger anzeigen
          </button>
        </div>
      </div>
    </section>

    <!-- Project Stats / Highlights Grid -->
    <section class="pb-32 md:pb-40 px-4 sm:px-6 lg:px-8 relative">
      <div class="max-w-7xl mx-auto mb-12 md:mb-16">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          <div v-for="(stat, i) in stats" :key="i" class="bg-white dark:bg-gray-800/80 backdrop-blur-md p-4 md:p-6 rounded-2xl md:rounded-3xl border-2 border-blue-100/60 dark:border-gray-600/50 shadow-lg text-center reveal hover:shadow-2xl hover:scale-105 hover:border-green-300/60 dark:hover:border-green-400 transition-all duration-300" :style="{ transitionDelay: `${i * 100}ms` }">
            <component :is="stat.icon" class="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 md:mb-4 text-[#e5544b] dark:text-red-400" />
            <div class="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1">{{ stat.value }}</div>
            <div class="text-xs md:text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wider font-semibold leading-tight">{{ stat.label }}</div>
          </div>
        </div>
      </div>
      
      <!-- Wellenförmige Trennlinie - verschmilzt von Stats Section zu Features Section -->
      <WaveDivider />
    </section>

    <!-- Features Section -->
    <section class="py-20 pb-32 md:pb-40 px-4 sm:px-6 lg:px-8 bg-blue-50/40 dark:bg-black/10 backdrop-blur-sm relative">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16 reveal">
          <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Highlights & Besonderheiten
          </h2>
          <div class="mt-4 h-2 w-24 bg-[#e5544b] mx-auto rounded-full"></div>
          <p class="mt-6 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Moderne Lösungen für einen authentischen Webauftritt
          </p>
        </div>

        <div class="space-y-16 md:space-y-24">
          <div
            v-for="(feature, index) in sortedFeatures"
            :key="feature.id"
            class="feature-item reveal"
            :style="{ transitionDelay: `${index * 100}ms` }"
          >
            <!-- Modern Card Layout: Mobile = Single Container, Desktop = Grid -->
            <div 
              class="relative group flex flex-col md:grid md:grid-cols-2 md:gap-8"
            >
              <!-- Mobile: Gemeinsamer Container für Text und Media -->
              <div class="md:hidden relative bg-gradient-to-br from-white via-white to-blue-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 backdrop-blur-md rounded-3xl shadow-2xl border-2 border-blue-100/60 dark:border-gray-700/50 group-hover:border-green-300/60 dark:group-hover:border-green-400 transition-all duration-500 overflow-hidden">
                <!-- Decorative Background Element -->
                <div class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#e5544b]/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                <!-- Text Content (oben auf mobil) -->
                <div class="relative p-6 pb-4">
                  <!-- Icon Badge -->
                  <div class="relative flex items-start justify-between mb-4">
                    <div class="flex items-center gap-3">
                      <div class="p-2.5 rounded-2xl bg-gradient-to-br from-[#e5544b]/10 to-red-600/10 dark:from-[#e5544b]/20 dark:to-red-600/20 border border-[#e5544b]/20 dark:border-red-500/20 group-hover:scale-110 transition-transform duration-300">
                        <component :is="feature.icon" class="w-6 h-6 text-[#e5544b] dark:text-red-400" />
                      </div>
                      <div class="px-3 py-1 rounded-full bg-gradient-to-r from-red-100 to-red-50 dark:from-red-900/40 dark:to-red-800/30 text-[#e5544b] dark:text-red-400 text-xs font-bold border border-[#e5544b]/20 dark:border-red-500/20">
                        #{{ String(index + 1).padStart(2, '0') }}
                      </div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h3 class="relative text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 group-hover:text-[#e5544b] dark:group-hover:text-red-400 transition-colors duration-300 leading-tight">
                    {{ feature.title }}
                  </h3>

                  <!-- Description -->
                  <p class="relative text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                    {{ feature.description }}
                  </p>

                    <!-- Tags -->
                    <div v-if="feature.tags" class="relative flex flex-wrap gap-2">
                      <span 
                        v-for="tag in feature.tags" 
                        :key="tag" 
                        class="text-xs font-semibold px-3 py-1 max-w-[45%] bg-blue-50/80 dark:bg-gray-700/60 rounded-full text-gray-700 dark:text-gray-300 border border-blue-200/50 dark:border-gray-600/50 group-hover:border-green-400/50 dark:group-hover:border-green-400 transition-colors duration-300"
                      >
                        {{ tag }}
                      </span>
                    </div>
                </div>

                <!-- Media Container (unten auf mobil) -->
                <div v-if="feature.type !== 'text-only'" class="relative w-full group/media px-4 pb-4">
                  <!-- Animated Background Glow -->
                  <div class="absolute -inset-4 bg-gradient-to-r from-[#e5544b]/20 via-red-600/20 to-[#e5544b]/20 rounded-[2rem] blur-2xl opacity-0 group-hover/media:opacity-100 transition-opacity duration-700 animate-pulse"></div>
                  
                  <!-- Media Container -->
                  <div class="relative w-full min-h-[300px] bg-gradient-to-br from-white via-blue-50 to-white dark:from-gray-800 dark:via-gray-900 dark:to-black rounded-2xl overflow-hidden shadow-xl border-2 border-blue-100/60 dark:border-gray-700/50 group-hover/media:border-green-300/60 dark:group-hover/media:border-green-400 transition-all duration-500 flex items-center justify-center p-3">
                    <!-- Video -->
                    <div v-if="feature.type === 'video'" class="w-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center">
                      <video
                        :src="feature.content"
                        controls
                        class="w-full max-h-[400px] object-contain rounded-lg"
                      >
                        Ihr Browser unterstützt das Video-Element nicht.
                      </video>
                    </div>

                    <!-- Single Image -->
                    <div v-else-if="feature.type === 'image' && feature.content" :class="['w-full relative cursor-pointer overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-xl', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-[400px]' : 'h-full min-h-[300px]']" @click="openLightbox(feature.content)">
                      <img
                        :src="feature.content"
                        :alt="feature.title"
                        :class="['rounded-lg group-hover/media:scale-105 transition-transform duration-700', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-full w-auto object-contain' : 'max-w-full max-h-full object-contain']"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/media:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg">
                          <MagnifyingGlassPlusIcon class="w-4 h-4 text-[#e5544b] dark:text-red-400" />
                          <span class="text-xs font-semibold text-gray-900 dark:text-white">Vergrößern</span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Image Slider for Gallery -->
                    <div v-else-if="feature.type === 'gallery' && 'images' in feature && feature.images" class="w-full min-h-[300px] relative bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden">
                      <div class="w-full min-h-[300px] overflow-hidden relative flex items-center justify-center">
                        <template v-for="(img, i) in feature.images" :key="`${feature.id}-${i}`">
                          <div 
                            v-show="(galleryStates[feature.id] ?? 0) === i"
                            class="absolute inset-0 cursor-pointer flex items-center justify-center p-2"
                            @click="openLightbox(img)"
                          >
                            <img 
                              :src="img" 
                              :alt="feature.title"
                              class="max-w-full max-h-full w-auto h-auto min-h-[280px] object-contain rounded-lg"
                            />
                          </div>
                        </template>
                      </div>

                      <!-- Slider Dots -->
                      <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                        <button 
                          v-for="(_, i) in feature.images" 
                          :key="i"
                          @click.stop="setGalleryIndex(feature.id, i)"
                          class="h-2 rounded-full transition-all duration-300"
                          :class="galleryStates[feature.id] === i ? 'bg-[#e5544b] w-6 shadow-lg shadow-[#e5544b]/50' : 'bg-gray-400/50 w-2 hover:bg-gray-400'"
                        ></button>
                      </div>

                      <!-- Slider Controls - Always visible on mobile -->
                      <button 
                        v-if="feature.type === 'gallery' && getGalleryImagesLength(feature) > 0"
                        @click.stop="prevGalleryImage(feature.id, getGalleryImagesLength(feature))"
                        class="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-[#e5544b] dark:text-red-400 opacity-100 md:opacity-0 md:group-hover/media:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 shadow-lg"
                      >
                        <ChevronLeftIcon class="w-5 h-5" />
                      </button>
                      <button 
                        v-if="feature.type === 'gallery' && getGalleryImagesLength(feature) > 0"
                        @click.stop="nextGalleryImage(feature.id, getGalleryImagesLength(feature))"
                        class="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-[#e5544b] dark:text-red-400 opacity-100 md:opacity-0 md:group-hover/media:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 shadow-lg"
                      >
                        <ChevronRightIcon class="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Hover Glow Effect -->
                <div class="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#e5544b]/0 via-[#e5544b]/0 to-[#e5544b]/0 group-hover:from-[#e5544b]/5 group-hover:via-transparent group-hover:to-transparent transition-all duration-500 pointer-events-none"></div>
              </div>

              <!-- Desktop: Feature Content Card -->
              <div 
                class="hidden md:block relative"
                :class="index % 2 === 1 && feature.type !== 'text-only' ? 'md:order-2' : feature.type === 'text-only' ? 'md:col-span-2' : ''"
              >
                <div class="relative h-full bg-gradient-to-br from-white via-white to-blue-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 backdrop-blur-md rounded-3xl p-10 shadow-2xl border-2 border-blue-100/60 dark:border-gray-700/50 group-hover:border-green-300/60 dark:group-hover:border-green-400 transition-all duration-500 overflow-hidden" :class="feature.type === 'text-only' ? 'min-h-[400px]' : ''">
                  <!-- Decorative Background Element -->
                  <div class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#e5544b]/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  
                  <!-- Icon Badge -->
                  <div class="relative flex items-start justify-between mb-6">
                    <div class="flex items-center gap-4">
                      <div class="p-3 rounded-2xl bg-gradient-to-br from-[#e5544b]/10 to-red-600/10 dark:from-[#e5544b]/20 dark:to-red-600/20 border border-[#e5544b]/20 dark:border-red-500/20 group-hover:scale-110 transition-transform duration-300">
                        <component :is="feature.icon" class="w-7 h-7 text-[#e5544b] dark:text-red-400" />
                      </div>
                      <div class="px-4 py-1.5 rounded-full bg-gradient-to-r from-red-100 to-red-50 dark:from-red-900/40 dark:to-red-800/30 text-[#e5544b] dark:text-red-400 text-xs font-bold border border-[#e5544b]/20 dark:border-red-500/20">
                        #{{ String(index + 1).padStart(2, '0') }}
                      </div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h3 class="relative text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6 group-hover:text-[#e5544b] dark:group-hover:text-red-400 transition-colors duration-300 leading-tight">
                    {{ feature.title }}
                  </h3>

                  <!-- Description -->
                  <p class="relative text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
                    {{ feature.description }}
                  </p>

                  <!-- Tags -->
                  <div v-if="feature.tags" class="relative flex flex-wrap gap-2.5">
                    <span 
                      v-for="tag in feature.tags" 
                      :key="tag" 
                      class="text-xs font-semibold px-4 py-1.5 bg-blue-50/80 dark:bg-gray-700/60 rounded-full text-gray-700 dark:text-gray-300 border border-blue-200/50 dark:border-gray-600/50 group-hover:border-green-400/50 dark:group-hover:border-green-400 transition-colors duration-300"
                    >
                      {{ tag }}
                    </span>
                  </div>

                  <!-- Hover Glow Effect -->
                  <div class="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#e5544b]/0 via-[#e5544b]/0 to-[#e5544b]/0 group-hover:from-[#e5544b]/5 group-hover:via-transparent group-hover:to-transparent transition-all duration-500 pointer-events-none"></div>
                </div>
              </div>

              <!-- Desktop: Feature Media Container -->
              <div 
                v-if="feature.type !== 'text-only'"
                class="hidden md:block relative"
                :class="index % 2 === 1 ? 'md:order-1' : ''"
              >
                <div class="relative w-full h-full min-h-[500px] flex items-center justify-center group/media">
                  <!-- Animated Background Glow -->
                  <div class="absolute -inset-6 bg-gradient-to-r from-[#e5544b]/20 via-red-600/20 to-[#e5544b]/20 rounded-[2.5rem] blur-2xl opacity-0 group-hover/media:opacity-100 transition-opacity duration-700 animate-pulse"></div>
                  
                  <!-- Media Container -->
                  <div class="relative w-full h-full bg-gradient-to-br from-white via-blue-50 to-white dark:from-gray-800 dark:via-gray-900 dark:to-black rounded-3xl overflow-hidden shadow-2xl border-2 border-blue-100/60 dark:border-gray-700/50 group-hover/media:border-green-300/60 dark:group-hover/media:border-green-400 transition-all duration-500 flex items-center justify-center p-6">
                    <!-- Video -->
                    <div v-if="feature.type === 'video'" class="w-full h-full bg-white dark:bg-gray-900 rounded-2xl overflow-hidden flex items-center justify-center">
                      <video
                        :src="feature.content"
                        controls
                        class="w-full h-full max-h-[500px] object-contain rounded-xl"
                      >
                        Ihr Browser unterstützt das Video-Element nicht.
                      </video>
                    </div>

                    <!-- Single Image -->
                    <div v-else-if="feature.type === 'image' && feature.content" :class="['w-full relative cursor-pointer overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900/50 rounded-2xl', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-[500px]' : 'h-full']" @click="openLightbox(feature.content)">
                      <img
                        :src="feature.content"
                        :alt="feature.title"
                        :class="['rounded-xl group-hover/media:scale-105 transition-transform duration-700', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-full w-auto object-contain' : 'max-w-full max-h-full object-contain']"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/media:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                        <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                          <MagnifyingGlassPlusIcon class="w-5 h-5 text-[#e5544b] dark:text-red-400" />
                          <span class="text-sm font-semibold text-gray-900 dark:text-white">Vergrößern</span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Image Slider for Gallery -->
                    <div v-else-if="feature.type === 'gallery' && 'images' in feature && feature.images" class="w-full h-full relative bg-gray-50 dark:bg-gray-900 rounded-2xl overflow-hidden">
                      <div class="h-full w-full overflow-hidden relative flex items-center justify-center">
                        <transition-group name="slide">
                          <div 
                            v-for="(img, i) in feature.images" 
                            v-show="galleryStates[feature.id] === i"
                            :key="img"
                            class="absolute inset-0 cursor-pointer flex items-center justify-center p-4"
                            @click="openLightbox(img)"
                          >
                            <img 
                              :src="img" 
                              :alt="feature.title"
                              class="max-w-full max-h-full object-contain rounded-xl"
                            />
                          </div>
                        </transition-group>
                      </div>

                      <!-- Slider Dots -->
                      <div class="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5 z-10">
                        <button 
                          v-for="(_, i) in feature.images" 
                          :key="i"
                          @click.stop="setGalleryIndex(feature.id, i)"
                          class="h-2 rounded-full transition-all duration-300"
                          :class="galleryStates[feature.id] === i ? 'bg-[#e5544b] w-8 shadow-lg shadow-[#e5544b]/50' : 'bg-gray-400/50 w-2 hover:bg-gray-400'"
                        ></button>
                      </div>

                      <!-- Slider Controls -->
                      <button 
                        v-if="feature.type === 'gallery' && getGalleryImagesLength(feature) > 0"
                        @click.stop="prevGalleryImage(feature.id, getGalleryImagesLength(feature))"
                        class="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-[#e5544b] dark:text-red-400 opacity-0 group-hover/media:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 shadow-lg"
                      >
                        <ChevronLeftIcon class="w-6 h-6" />
                      </button>
                      <button 
                        v-if="feature.type === 'gallery' && getGalleryImagesLength(feature) > 0"
                        @click.stop="nextGalleryImage(feature.id, getGalleryImagesLength(feature))"
                        class="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-[#e5544b] dark:text-red-400 opacity-0 group-hover/media:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 shadow-lg"
                      >
                        <ChevronRightIcon class="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Wellenförmige Trennlinie - verschmilzt von Features Section zu Project Info Grid -->
      <WaveDivider :show="shouldShowDivider" />
    </section>

    <!-- Project Info Grid -->
    <section class="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50/40 dark:bg-black/10 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-blue-100/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-2xl hover:border-green-300/60 dark:hover:border-green-400 transition-all duration-300 reveal">
            <h4 class="text-[#e5544b] dark:text-red-400 font-bold mb-4 uppercase tracking-wider text-sm">Plattform</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">WordPress</p>
          </div>
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-blue-100/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-2xl hover:border-green-300/60 dark:hover:border-green-400 transition-all duration-300 reveal delay-100">
            <h4 class="text-[#e5544b] dark:text-red-400 font-bold mb-4 uppercase tracking-wider text-sm">Fokus</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Performance & Pflegebarkeit</p>
          </div>
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-blue-100/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-2xl hover:border-green-300/60 dark:hover:border-green-400 transition-all duration-300 reveal delay-200">
            <h4 class="text-[#e5544b] dark:text-red-400 font-bold mb-4 uppercase tracking-wider text-sm">Zielgruppe</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Familien & Besucher</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Back to Projekte -->
    <section class="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50/40 dark:bg-black/10 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto text-center reveal">
        <router-link
          to="/portfolio"
          class="inline-flex items-center space-x-3 px-10 py-4 bg-white dark:bg-gray-800 text-[#e5544b] dark:text-red-400 font-bold rounded-2xl shadow-xl hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-300 hover:scale-105 border-2 border-[#e5544b]/30 dark:border-red-400/30 hover:border-green-400/50 dark:hover:border-green-400/50"
        >
          <ArrowLeftIcon class="w-5 h-5" />
          <span>Zurück zur Übersicht</span>
        </router-link>
      </div>
    </section>

    <!-- Lightbox Modal -->
    <Transition name="fade">
      <div v-if="lightboxUrl" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" @click="closeLightbox">
        <button class="absolute top-6 right-6 text-white hover:text-[#e5544b] transition-colors p-2" @click="closeLightbox">
          <XMarkIcon class="w-8 h-8" />
        </button>
        <img :src="lightboxUrl" alt="Lightbox Image" class="max-w-full max-h-full object-contain rounded-lg shadow-2xl" @click.stop />
      </div>
    </Transition>

    <StandardFooter accent-color="#e5544b" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeMount, onUnmounted, nextTick, watch } from 'vue'
import Navigation from '../../components/Navigation.vue'
import StandardFooter from '../../components/StandardFooter.vue'
import WaveDivider from '../../components/WaveDivider.vue'
import { useTheme } from '../../composables/useTheme'
import { getPortfolioProjectByPageUrl } from '../../firebase/portfolio'
import { logger } from '../../utils/logger'
import { 
  ArrowLeftIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  MagnifyingGlassPlusIcon,
  XMarkIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  PaintBrushIcon,
  RocketLaunchIcon,
  SparklesIcon,
  Cog6ToothIcon
} from '@heroicons/vue/24/outline'
import type { Component } from 'vue'

const { isDark } = useTheme()

// Logo aus Firebase laden
const logoUrl = ref<string>('')

// Logo aktualisieren, wenn Theme wechselt
const updateLogo = async () => {
  try {
    const project = await getPortfolioProjectByPageUrl('/projects/reitsberger-hof')
    if (project) {
      if (isDark.value && project.logoDark) {
        logoUrl.value = project.logoDark
      } else if (!isDark.value && project.logoLight) {
        logoUrl.value = project.logoLight
      } else {
        logoUrl.value = ''
      }
    } else {
      logoUrl.value = ''
    }
  } catch (error) {
    logger.error('Fehler beim Laden der Projekt-Daten', error)
    logoUrl.value = ''
  }
}

// Alias für initiales Laden
const loadProjectData = updateLogo

// Watch für Theme-Änderungen - außerhalb von onMounted für sofortige Reaktion
const stopWatch = watch(isDark, () => {
  updateLogo()
}, { immediate: false })

// Feature Type Definitions
interface BaseFeature {
  id: string
  title: string
  description: string
  tags?: string[]
  icon: Component
  order: number
}

interface ImageFeature extends BaseFeature {
  type: 'image'
  content: string
  mobileImageStyle?: boolean
}

interface VideoFeature extends BaseFeature {
  type: 'video'
  content: string
}

interface GalleryFeature extends BaseFeature {
  type: 'gallery'
  images: string[]
}

interface TextOnlyFeature extends BaseFeature {
  type: 'text-only'
  content: string
}

type ProjectFeature = ImageFeature | VideoFeature | GalleryFeature | TextOnlyFeature

// Type Guard für Gallery Features
const isGalleryFeature = (feature: ProjectFeature): feature is GalleryFeature => {
  return feature.type === 'gallery' && 'images' in feature && Array.isArray(feature.images)
}

// Helper-Funktion für Template-Zugriff auf images.length
const getGalleryImagesLength = (feature: ProjectFeature): number => {
  if (isGalleryFeature(feature)) {
    return feature.images.length
  }
  return 0
}

const showAllTechs = ref(false)
const isMobile = ref(false)

const checkIfMobile = () => {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

const displayedTechnologies = computed(() => {
  // Auf Desktop immer alle Tags anzeigen
  if (!isMobile.value) {
    return project.value.technologies
  }
  // Auf mobil: Wenn "Mehr anzeigen" geklickt wurde, alle anzeigen, sonst nur erste 8
  if (showAllTechs.value) {
    return project.value.technologies
  }
  return project.value.technologies.slice(0, 8)
})

const stats = [
  { icon: PaintBrushIcon, value: 'Modern', label: 'Design' },
  { icon: DevicePhoneMobileIcon, value: '100%', label: 'Responsive' },
  { icon: RocketLaunchIcon, value: 'Optimiert', label: 'Performance' },
  { icon: SparklesIcon, value: 'Einfach', label: 'Pflegebar' }
]

interface Project {
  technologies: string[]
  features: ProjectFeature[]
}

const project = ref<Project>({
  technologies: ['WordPress', 'Elementor', 'Responsive Design', 'Performance', 'SEO', 'UX', 'Mobile First', 'Accessibility'],
  features: [
    {
      id: 'feature-design',
      title: 'Modernes & Ansprechendes Design',
      description: 'Die Webseite wurde mit einem modernen, benutzerfreundlichen Design konzipiert, das Tradition und Regionalität mit einer zeitgemäßen digitalen Außendarstellung verbindet. Das Design vermittelt Authentizität und schafft Vertrauen durch Transparenz und persönliche Nähe.',
      type: 'image',
      content: '/projects/reitsberger-hof/hero.webp',
      tags: ['Design', 'UX', 'Branding'],
      icon: PaintBrushIcon,
      order: 1
    },
    {
      id: 'feature-responsive',
      title: 'Vollständig Responsive',
      description: 'Die Webseite wurde mit einem Mobile-First-Ansatz entwickelt und ist auf allen Geräten optimal nutzbar - von Smartphones über Tablets bis hin zu Desktop-Computern. Das responsive Design sorgt für eine konsistente und angenehme Nutzererfahrung auf allen Bildschirmgrößen.',
      type: 'image',
      content: '/projects/reitsberger-hof/Mobile.webp',
      tags: ['Responsive', 'Mobile First', 'Cross-Platform'],
      icon: DevicePhoneMobileIcon,
      order: 2,
      mobileImageStyle: true
    },
    {
      id: 'feature-wordpress',
      title: 'WordPress & Elementor - Einfache Content-Pflege',
      description: 'Die Webseite wurde mit WordPress und Elementor umgesetzt, um eine schnelle Anpassbarkeit und leichte Bearbeitbarkeit zu gewährleisten. Der Betreiber kann Inhalte selbstständig pflegen und anpassen - neue Angebote, Aktuelles oder Bilder können ohne technisches Know-how hinzugefügt oder aktualisiert werden. Die intuitive Bedienoberfläche von Elementor macht die Content-Pflege zum Kinderspiel.',
      type: 'image',
      content: '/projects/reitsberger-hof/Edit.webp',
      tags: ['WordPress', 'Elementor', 'CMS', 'Content Management'],
      icon: Cog6ToothIcon,
      order: 3
    },
    {
      id: 'feature-performance-seo',
      title: 'Performance & SEO-Optimierung',
      description: 'Besonderes Augenmerk wurde auf schnelle Ladezeiten und optimale Performance gelegt. Durch gezielte Optimierungen lädt die Webseite schnell und bietet Besuchern eine flüssige Nutzererfahrung. Zusätzlich wurde die Webseite suchmaschinenoptimiert entwickelt, um die Sichtbarkeit des Reitsberger Hofs zu verbessern. Strukturierte Inhalte, optimierte Meta-Tags und eine klare Seitenstruktur maximieren die Auffindbarkeit für potenzielle Besucher.',
      type: 'text-only',
      content: '',
      tags: ['Performance', 'SEO', 'Optimization', 'Speed', 'Search Engine'],
      icon: RocketLaunchIcon,
      order: 4
    }
  ]
})

// Gallery Slider Logic
const galleryStates = ref<Record<string, number>>({})
const galleryIntervals: Record<string, any> = {}

const initGalleries = () => {
  project.value.features.forEach(f => {
    if (isGalleryFeature(f)) {
      galleryStates.value[f.id] = 0
      startGalleryTimer(f.id, f.images.length)
    }
  })
}

const startGalleryTimer = (id: string, length: number) => {
  stopGalleryTimer(id)
  galleryIntervals[id] = setInterval(() => {
    nextGalleryImage(id, length)
  }, 5000)
}

const stopGalleryTimer = (id: string) => {
  if (galleryIntervals[id]) {
    clearInterval(galleryIntervals[id])
  }
}

const nextGalleryImage = (id: string, length: number) => {
  galleryStates.value[id] = (galleryStates.value[id] + 1) % length
  startGalleryTimer(id, length)
}

const prevGalleryImage = (id: string, length: number) => {
  galleryStates.value[id] = (galleryStates.value[id] - 1 + length) % length
  startGalleryTimer(id, length)
}

const setGalleryIndex = (id: string, index: number) => {
  galleryStates.value[id] = index
  const f = project.value.features.find(feat => feat.id === id)
  if (f && isGalleryFeature(f)) {
    startGalleryTimer(id, f.images.length)
  }
}

// Lightbox Logic
const lightboxUrl = ref<string | null>(null)
const openLightbox = (url: string) => {
  lightboxUrl.value = url
  document.body.style.overflow = 'hidden'
}
const closeLightbox = () => {
  lightboxUrl.value = null
  document.body.style.overflow = ''
}

// Scroll Animation Logic
const initScrollReveal = () => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active')
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal').forEach(el => observer.observe(el))
}

const sortedFeatures = computed(() => {
  return [...project.value.features].sort((a, b) => a.order - b.order)
})

/**
 * Bestimmt, ob die Trennlinie zwischen Features Section und Project Info Grid angezeigt werden soll.
 * 
 * Die Trennlinie wird nur angezeigt, wenn sich die Hintergrundfarben der beiden Sections unterscheiden.
 * Aktuell haben beide Sections die gleiche Hintergrundfarbe (bg-blue-50/40 dark:bg-gray-800/40),
 * daher wird die Trennlinie standardmäßig ausgeblendet.
 * 
 * @returns {boolean} true wenn die Trennlinie angezeigt werden soll, sonst false
 */
const shouldShowDivider = computed(() => {
  // Beide Sections haben aktuell die gleiche Hintergrundfarbe
  // Setze auf true, wenn sich die Farben unterscheiden sollen
  return false
})


const handleResize = () => {
  isMobile.value = checkIfMobile()
  // Auf Desktop immer alle Tags anzeigen
  if (!isMobile.value) {
    showAllTechs.value = true
  }
}

// Handler für pageshow Event (wird beim Aktualisieren ausgelöst)
const handlePageShow = (event: PageTransitionEvent) => {
  // Wenn die Seite aus dem Cache kommt (z.B. beim Aktualisieren), scroll nach oben
  if (event.persisted) {
    window.scrollTo({ top: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }
}

onBeforeMount(() => {
  // Stelle sicher, dass die Seite oben startet - bereits vor dem Mount
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }
})

onMounted(async () => {
  // Stelle sicher, dass die Seite oben startet - auch nach dem Mount
  window.scrollTo({ top: 0, behavior: 'instant' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  
  // Zusätzlich nach nextTick, um sicherzustellen, dass das DOM bereit ist
  nextTick(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  })
  
  // Lade Projekt-Daten und Logo
  await loadProjectData()
  
  // Prüfe ob mobil
  isMobile.value = checkIfMobile()
  if (!isMobile.value) {
    showAllTechs.value = true
  }
  
  // Listener für Resize hinzufügen
  window.addEventListener('resize', handleResize)
  // Listener für pageshow hinzufügen (wird beim Aktualisieren ausgelöst)
  window.addEventListener('pageshow', handlePageShow)
  
  initGalleries()
  initScrollReveal()
  // Füge Klasse zu html hinzu für Scrollbar-Styling
  document.documentElement.classList.add('reitsberger-hof-page')
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('pageshow', handlePageShow)
  Object.values(galleryIntervals).forEach(clearInterval)
  // Stoppe Watch für Theme-Änderungen
  stopWatch()
  // Entferne Klasse von html
  document.documentElement.classList.remove('reitsberger-hof-page')
})
</script>

<style scoped>
@keyframes blob-organic {
  0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
  33% { transform: translate(40px, -60px) scale(1.15) rotate(5deg); }
  66% { transform: translate(-30px, 30px) scale(0.95) rotate(-5deg); }
  100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
}

.animate-blob-organic {
  animation: blob-organic 10s infinite ease-in-out;
}

/* Zusätzliche Animation für natürlichere Bewegung */
@keyframes blob-natural {
  0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
  25% { transform: translate(30px, 40px) scale(1.1) rotate(3deg); }
  50% { transform: translate(-20px, -30px) scale(0.9) rotate(-3deg); }
  75% { transform: translate(20px, 20px) scale(1.05) rotate(2deg); }
  100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
}

.animate-blob-natural {
  animation: blob-natural 12s infinite ease-in-out;
}

.animation-delay-1500 {
  animation-delay: 1.5s;
}

.animation-delay-1500 {
  animation-delay: 1.5s;
}

.animation-delay-3000 {
  animation-delay: 3s;
}

/* Scroll Reveal */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

.delay-100 { transition-delay: 0.1s; }
.delay-200 { transition-delay: 0.2s; }
.delay-300 { transition-delay: 0.3s; }
.delay-400 { transition-delay: 0.4s; }
.delay-500 { transition-delay: 0.5s; }

/* Slide Animation */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.8s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Fade Animation (Lightbox) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Mauszeiger und Scrollwheel in Featurefarbe für Reitsberger Hof Seite */
:deep(.outer-ring) {
  border-color: rgba(229, 84, 75, 0.3) !important;
}

:deep(.outer-ring.hover-active) {
  border-color: rgba(229, 84, 75, 0.4) !important;
}

:deep(.custom-cursor) {
  background: rgba(229, 84, 75, 0.6) !important;
  box-shadow: 
    0 0 10px rgba(229, 84, 75, 0.5),
    0 0 20px rgba(229, 84, 75, 0.3),
    inset 0 0 10px rgba(229, 84, 75, 0.2) !important;
}

:deep(.custom-cursor.hover-active) {
  background: rgba(229, 84, 75, 0.7) !important;
  box-shadow: 
    0 0 15px rgba(229, 84, 75, 0.6),
    0 0 30px rgba(229, 84, 75, 0.4),
    0 0 45px rgba(229, 84, 75, 0.2),
    inset 0 0 15px rgba(229, 84, 75, 0.3) !important;
}

:deep(.trail-dot) {
  background: rgba(229, 84, 75, 0.5) !important;
}
</style>

<style>
/* Scrollwheel in Featurefarbe für Reitsberger Hof Seite */
html.reitsberger-hof-page,
html.reitsberger-hof-page * {
  scrollbar-width: thin;
  scrollbar-color: rgba(229, 84, 75, 0.6) rgba(0, 0, 0, 0.05) !important;
}

html.reitsberger-hof-page ::-webkit-scrollbar,
html.reitsberger-hof-page body ::-webkit-scrollbar {
  width: 12px !important;
  height: 12px !important;
}

html.reitsberger-hof-page ::-webkit-scrollbar-track,
html.reitsberger-hof-page body ::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05) !important;
  border-left: none !important;
  border-radius: 0 !important;
}

html.reitsberger-hof-page ::-webkit-scrollbar-thumb,
html.reitsberger-hof-page body ::-webkit-scrollbar-thumb {
  background: rgba(229, 84, 75, 0.6) !important;
  background-image: none !important;
  border-radius: 6px !important;
  border: 2px solid transparent !important;
  background-clip: padding-box !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.reitsberger-hof-page ::-webkit-scrollbar-thumb:hover,
html.reitsberger-hof-page body ::-webkit-scrollbar-thumb:hover {
  background: rgba(229, 84, 75, 0.8) !important;
  background-image: none !important;
  background-clip: padding-box !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.reitsberger-hof-page ::-webkit-scrollbar-thumb:active,
html.reitsberger-hof-page body ::-webkit-scrollbar-thumb:active {
  background: rgba(229, 84, 75, 0.9) !important;
  background-image: none !important;
  box-shadow: none !important;
  transform: none !important;
}

/* Dark Mode Scrollwheel für Reitsberger Hof Seite */
html.reitsberger-hof-page.dark,
html.reitsberger-hof-page.dark * {
  scrollbar-color: rgba(229, 84, 75, 0.7) rgba(255, 255, 255, 0.05) !important;
}

html.reitsberger-hof-page.dark ::-webkit-scrollbar-track,
html.reitsberger-hof-page.dark body ::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05) !important;
  border-left: none !important;
}

html.reitsberger-hof-page.dark ::-webkit-scrollbar-thumb,
html.reitsberger-hof-page.dark body ::-webkit-scrollbar-thumb {
  background: rgba(229, 84, 75, 0.7) !important;
  background-image: none !important;
  box-shadow: none !important;
  animation: none !important;
}

html.reitsberger-hof-page.dark ::-webkit-scrollbar-thumb:hover,
html.reitsberger-hof-page.dark body ::-webkit-scrollbar-thumb:hover {
  background: rgba(229, 84, 75, 0.9) !important;
  background-image: none !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.reitsberger-hof-page.dark ::-webkit-scrollbar-thumb:active,
html.reitsberger-hof-page.dark body ::-webkit-scrollbar-thumb:active {
  background: rgba(229, 84, 75, 1) !important;
  background-image: none !important;
  box-shadow: none !important;
  transform: none !important;
}

/* IT Text Unterstreichung in Reitsberger Hof Farbe */
html.reitsberger-hof-page .it-text::after {
  background-color: rgb(229, 84, 75) !important; /* #e5544b */
}

html.reitsberger-hof-page.dark .it-text::after {
  background-color: rgb(248, 113, 113) !important; /* red-400 */
}
</style>

